import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Calendar,
  CalendarClock,
  MessageCircle,
  FolderLock,
  CreditCard,
  CheckCircle2,
  ArrowRight,
  FileText,
  Send,
  Building2,
  Download,
} from "lucide-react";
import { getMyDashboard, getMyBookings, resendMyBookingReceipt } from "@/lib/api/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/")({
  component: Dashboard,
});

const statusStyles: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-900 border-yellow-300",
  confirmed: "bg-blue-100 text-blue-900 border-blue-300",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  cancelled: "bg-red-100 text-red-900 border-red-300",
};

const paymentStyles: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-900 border-emerald-300",
  unpaid: "bg-yellow-100 text-yellow-900 border-yellow-300",
  partial: "bg-amber-100 text-amber-900 border-amber-300",
  refunded: "bg-slate-200 text-slate-800 border-slate-300",
};

function Dashboard() {
  const fetchDashboard = useServerFn(getMyDashboard);
  const fetchBookings = useServerFn(getMyBookings);
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-dashboard"],
    queryFn: () => fetchDashboard(),
  });
  // Prefetch full bookings list for the "All bookings" section below
  const { data: bookingsData } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => fetchBookings(),
  });

  if (error) {
    return <p className="text-sm text-red-600">Could not load your dashboard.</p>;
  }

  const c = data?.counts;

  return (
    <section className="space-y-8">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-navy">Dashboard</h2>
          <p className="text-sm text-muted-foreground">
            Your bookings, enquiries, documents and payment status — all in one place.
          </p>
        </div>
        <Link
          to="/book"
          className="inline-flex items-center gap-2 rounded-md bg-navy px-3 py-2 text-sm text-navy-foreground hover:bg-navy/90"
        >
          <CalendarClock className="h-4 w-4" /> New booking
        </Link>
      </div>

      {/* Stat grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Calendar}
          label="Upcoming bookings"
          value={c?.bookingsUpcoming ?? 0}
          hint={`${c?.bookingsTotal ?? 0} total`}
          to="/portal"
          tone="navy"
        />
        <StatCard
          icon={CreditCard}
          label="Payments confirmed"
          value={c?.paymentsConfirmed ?? 0}
          hint={`${c?.paymentsPending ?? 0} pending`}
          tone="emerald"
        />
        <StatCard
          icon={MessageCircle}
          label="Enquiries"
          value={c?.enquiriesTotal ?? 0}
          hint="Tied to your email"
          tone="blue"
        />
        <StatCard
          icon={FolderLock}
          label="My documents"
          value={c?.documentsTotal ?? 0}
          hint={`${c?.docsPaid ?? 0} paid · ${c?.docsUnpaid ?? 0} due`}
          to="/portal/documents"
          tone="gold"
        />
      </div>

      {/* Company assets are confidential corporate records — restricted to admin users only. */}


      {/* Quick links */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <QuickLink to="/portal/cv" icon={FileText} label="AI CV Builder" />
        <QuickLink to="/portal/documents" icon={FolderLock} label="Upload documents" />
        <QuickLink to="/portal/academy" icon={CheckCircle2} label="NDLCS Academy" />
        <QuickLink to="/portal/chat" icon={MessageCircle} label="Live chat" />
      </div>

      {/* Upcoming bookings */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg text-navy">Upcoming bookings</h3>
        </div>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && data.upcoming.length === 0 && (
          <EmptyState
            text="No upcoming consultations."
            ctaText="Book one now"
            ctaTo="/book"
          />
        )}
        <div className="space-y-2">
          {data?.upcoming.map((b: any) => (
            <div
              key={b.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 text-sm"
            >
              <div>
                <div className="font-medium text-navy">{b.service}</div>
                <div className="text-xs text-muted-foreground">
                  {b.preferred_date} · {b.preferred_time} · {b.consultation_type}
                </div>
              </div>
              <span
                className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusStyles[b.status] ?? statusStyles.pending}`}
              >
                {b.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent documents w/ payment status */}
      <div>
        <h3 className="mb-3 font-display text-lg text-navy">Recent documents & payments</h3>
        {data && data.recentDocuments.length === 0 && (
          <EmptyState
            text="No documents uploaded yet."
            ctaText="Upload a document"
            ctaTo="/portal/documents"
          />
        )}
        <div className="overflow-x-auto rounded-lg border border-border">
          {data && data.recentDocuments.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">File</th>
                  <th className="px-3 py-2 text-left">Matter</th>
                  <th className="px-3 py-2 text-left">Payment</th>
                </tr>
              </thead>
              <tbody>
                {data.recentDocuments.map((d: any) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{d.case_reference ?? "—"}</td>
                    <td className="px-3 py-2">{d.original_filename ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {d.case_status ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-xs font-medium ${paymentStyles[d.payment_status] ?? "bg-muted text-muted-foreground border-border"}`}
                      >
                        {d.payment_status ?? "n/a"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* All bookings list */}
      <div>
        <h3 className="mb-3 font-display text-lg text-navy">All bookings</h3>
        <div className="space-y-2">
          {bookingsData?.bookings.map((b: any) => (
            <BookingRow key={b.id} b={b} />
          ))}
          {bookingsData && bookingsData.bookings.length === 0 && (
            <p className="text-sm text-muted-foreground">No bookings yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  to,
  tone,
}: {
  icon: any;
  label: string;
  value: number;
  hint?: string;
  to?: string;
  tone: "navy" | "emerald" | "blue" | "gold";
}) {
  const tones: Record<string, string> = {
    navy: "bg-navy/5 text-navy",
    emerald: "bg-emerald-50 text-emerald-900",
    blue: "bg-blue-50 text-blue-900",
    gold: "bg-gold/10 text-navy",
  };
  const body = (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
      <div className={`rounded-md p-2 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="text-2xl font-semibold text-navy">{value}</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </div>
      {to && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
  return to ? (
    <Link to={to as any} className="block hover:opacity-90">
      {body}
    </Link>
  ) : (
    body
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: any; label: string }) {
  return (
    <Link
      to={to as any}
      className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-3 text-sm hover:border-navy/40"
    >
      <Icon className="h-4 w-4 text-gold" />
      <span className="font-medium text-navy">{label}</span>
      <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

function EmptyState({
  text,
  ctaText,
  ctaTo,
}: {
  text: string;
  ctaText: string;
  ctaTo: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm">
      <span className="text-muted-foreground">{text}</span>
      <Link
        to={ctaTo as any}
        className="rounded-md bg-navy px-3 py-1.5 text-xs text-navy-foreground"
      >
        {ctaText}
      </Link>
    </div>
  );
}

function BookingRow({ b }: { b: any }) {
  const resend = useServerFn(resendMyBookingReceipt);
  const onResend = async () => {
    try {
      await resend({ data: { id: b.id } });
      toast.success("Receipt sent to your email");
    } catch (e: any) {
      toast.error("Could not resend", { description: e?.message });
    }
  };
  const payStyle =
    paymentStyles[b.payment_status as string] ??
    "bg-muted text-muted-foreground border-border";
  const updated = b.payment_updated_at
    ? new Date(b.payment_updated_at).toLocaleString()
    : null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 text-sm">
      <div className="min-w-0">
        <div className="font-medium text-navy">{b.service}</div>
        <div className="text-xs text-muted-foreground">
          {b.preferred_date} · {b.preferred_time} · {b.consultation_type}
        </div>
        {updated && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Payment updated: {updated}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusStyles[b.status] ?? statusStyles.pending}`}
        >
          {b.status}
        </span>
        <span
          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${payStyle}`}
        >
          {b.payment_status ?? "unpaid"}
        </span>
        <button
          type="button"
          onClick={onResend}
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs hover:border-navy/40"
          title="Resend receipt / calendar invite to your email"
        >
          <Send className="h-3 w-3" /> Resend
        </button>
      </div>
    </div>
  );
}
